import manifest from '../assets/images/manifest.json'

/**
 * One picture, in the two formats `tools/optimise-images.mjs` emits, with the
 * dimensions it emitted them at. Dimensions travel with the asset so no page
 * has to hardcode numbers that the next conversion would silently invalidate.
 */
export interface ImageAsset {
  /** Preferred format — served to every browser that understands it. */
  webp: string
  /** JPEG for photographs, PNG where transparency is real. */
  fallback: string
  width: number
  height: number
}

type Manifest = Record<string, { width: number; height: number }>

// Vite content-hashes imported assets, so a replaced photograph under an
// unchanged name gets a new URL instead of being served stale from caches
// forever. That is why these live in src/assets and not in public/.
const URLS = import.meta.glob('../assets/images/**/*.{webp,jpg,png}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const KEY = /^\.\.\/assets\/images\/(.+)\.(webp|jpg|png)$/

function build(): Record<string, ImageAsset> {
  const parts: Record<string, { webp?: string; fallback?: string }> = {}
  for (const [path, url] of Object.entries(URLS)) {
    const m = KEY.exec(path)
    if (!m) continue
    const [, name, ext] = m as unknown as [string, string, string]
    const entry = (parts[name] ??= {})
    if (ext === 'webp') entry.webp = url
    else entry.fallback = url
  }

  const assets: Record<string, ImageAsset> = {}
  for (const [name, { webp, fallback }] of Object.entries(parts)) {
    const size = (manifest as Manifest)[name]
    // A half-emitted asset is a broken pipeline, not a missing picture — say so
    // at import time rather than rendering a hole at runtime.
    if (!webp || !fallback || !size) continue
    assets[name] = { webp, fallback, width: size.width, height: size.height }
  }
  return assets
}

const ASSETS = build()

/** Every emitted image, keyed as in the manifest (e.g. `hospitals/ugmc-main`). */
export function allAssets(): Readonly<Record<string, ImageAsset>> {
  return ASSETS
}

/**
 * Look up one emitted image. Throws on an unknown name: a typo'd asset path is
 * a bug to fix, not an empty box to ship.
 */
export function asset(name: string): ImageAsset {
  const found = ASSETS[name]
  if (!found) {
    throw new Error(
      `No image asset "${name}". Run tools/optimise-images.mjs, or check the name against frontend/src/assets/images/manifest.json.`,
    )
  }
  return found
}
